import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';

// Categorical slots 1 and 2, validated as a pair on the white page surface
// (colour blind deltaE 24.7, contrast >= 3:1)
export const COMMUNITY_COLOR = '#2a78d6';
export const COMMERCIAL_COLOR = '#eb6834';

const SURFACE = '#ffffff';
const INK = '#24292f';
const MUTED_INK = '#57606a';
const GRID = '#eaecef';
const AXIS = '#d0d7de';

const DEFAULT_WIDTH = 900;
const HEIGHT = 240;
const PADDING_LEFT = 52;
const PADDING_RIGHT = 12;
const PADDING_TOP = 12;
const AXIS_BAND = 26;
const MAX_BAR_WIDTH = 24;
const SEGMENT_GAP = 2;
const CORNER = 4;

const formatTokens = value =>
  (value || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

const formatDate = date =>
  new Date(`${date}T12:00:00`).toLocaleDateString([], {
    day: 'numeric',
    month: 'short'
  });

// Gridlines on round numbers
const niceCeiling = value => {
  if (value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  return Math.ceil(value / magnitude) * magnitude;
};

// A bar with only its top corners rounded, so it sits square on the baseline
const barPath = (x, y, width, height, rounded) => {
  if (height <= 0) return '';
  const radius = rounded ? Math.min(CORNER, height, width / 2) : 0;
  return [
    `M${x},${y + height}`,
    `L${x},${y + radius}`,
    radius ? `Q${x},${y} ${x + radius},${y}` : '',
    `L${x + width - radius},${y}`,
    radius ? `Q${x + width},${y} ${x + width},${y + radius}` : '',
    `L${x + width},${y + height}`,
    'Z'
  ]
    .filter(part => part)
    .join(' ');
};

function TokenSplitChart({ data }) {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(DEFAULT_WIDTH);
  const [activeDate, setActiveDate] = useState(null);

  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) return undefined;

    const update = () => setWidth(element.clientWidth || DEFAULT_WIDTH);
    update();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }

    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const layout = useMemo(
    () => {
      const points = data || [];
      if (!points.length) return null;

      const plotLeft = PADDING_LEFT;
      const plotRight = Math.max(width - PADDING_RIGHT, plotLeft + 1);
      const baseline = HEIGHT - AXIS_BAND;
      const plotHeight = baseline - PADDING_TOP;

      const maxTotal = points.reduce(
        (max, point) =>
          Math.max(
            max,
            (point.communityTokens || 0) + (point.commercialTokens || 0)
          ),
        0
      );
      const scaleMax = niceCeiling(maxTotal);
      const bandWidth = (plotRight - plotLeft) / points.length;
      const barWidth = Math.max(2, Math.min(MAX_BAR_WIDTH, bandWidth * 0.7));

      // A tick roughly every 90px, on a whole number of days
      const tickStep = Math.max(
        1,
        Math.round(
          points.length / Math.max(1, Math.floor((plotRight - plotLeft) / 90))
        )
      );

      return {
        points: points.map((point, index) => ({
          ...point,
          x: plotLeft + bandWidth * index + (bandWidth - barWidth) / 2,
          bandX: plotLeft + bandWidth * index,
          showTick: index % tickStep === 0
        })),
        bandWidth,
        barWidth,
        baseline,
        plotLeft,
        plotRight,
        scaleMax,
        toHeight: value => (value / scaleMax) * plotHeight,
        gridValues: [0, 0.25, 0.5, 0.75, 1].map(step => step * scaleMax)
      };
    },
    [data, width]
  );

  if (!layout) {
    return <p style={styles.empty}>No tokens in this period.</p>;
  }

  const active = layout.points.find(point => point.date === activeDate);

  return (
    <div ref={containerRef} style={styles.container}>
      <svg
        width={width}
        height={HEIGHT}
        role="img"
        aria-label="Tokens earned per day, community and commercial surveys"
        style={styles.svg}
      >
        {layout.gridValues.map(value => {
          const y = layout.baseline - layout.toHeight(value);
          return (
            <g key={`grid-${value}`}>
              <line
                x1={layout.plotLeft}
                x2={layout.plotRight}
                y1={y}
                y2={y}
                stroke={value === 0 ? AXIS : GRID}
                strokeWidth={1}
              />
              <text
                x={layout.plotLeft - 8}
                y={y + 4}
                textAnchor="end"
                fontSize={11}
                fill={MUTED_INK}
                style={styles.tabular}
              >
                {formatTokens(Math.round(value))}
              </text>
            </g>
          );
        })}

        {layout.points.map(point => {
          const community = point.communityTokens || 0;
          const commercial = point.commercialTokens || 0;
          const communityHeight = layout.toHeight(community);
          const commercialHeight = layout.toHeight(commercial);
          const dimmed = activeDate && activeDate !== point.date;
          // The gap between the two segments is surface, not a stroke
          const gap = community > 0 && commercial > 0 ? SEGMENT_GAP : 0;

          return (
            <g key={point.date} opacity={dimmed ? 0.35 : 1}>
              {community > 0 ? (
                <path
                  d={barPath(
                    point.x,
                    layout.baseline - communityHeight,
                    layout.barWidth,
                    communityHeight,
                    commercial <= 0
                  )}
                  fill={COMMUNITY_COLOR}
                />
              ) : null}
              {commercial > 0 ? (
                <path
                  d={barPath(
                    point.x,
                    layout.baseline - communityHeight - commercialHeight,
                    layout.barWidth,
                    Math.max(0, commercialHeight - gap),
                    true
                  )}
                  fill={COMMERCIAL_COLOR}
                />
              ) : null}
            </g>
          );
        })}

        {layout.points.map(point =>
          point.showTick ? (
            <text
              key={`tick-${point.date}`}
              x={point.x + layout.barWidth / 2}
              y={layout.baseline + 16}
              textAnchor="middle"
              fontSize={11}
              fill={MUTED_INK}
              style={styles.tabular}
            >
              {formatDate(point.date)}
            </text>
          ) : null
        )}

        {/* The hit area is the whole band, so the pointer never has to find a thin bar */}
        {layout.points.map(point => (
          <rect
            key={`hit-${point.date}`}
            x={point.bandX}
            y={PADDING_TOP}
            width={Math.max(layout.bandWidth, 1)}
            height={layout.baseline - PADDING_TOP}
            fill="transparent"
            tabIndex={0}
            aria-label={`${formatDate(point.date)}: ${formatTokens(
              point.communityTokens
            )} community, ${formatTokens(point.commercialTokens)} commercial`}
            onMouseEnter={() => setActiveDate(point.date)}
            onMouseLeave={() => setActiveDate(null)}
            onFocus={() => setActiveDate(point.date)}
            onBlur={() => setActiveDate(null)}
            style={styles.hit}
          />
        ))}
      </svg>

      {active ? (
        <div
          style={{
            ...styles.tooltip,
            left: Math.min(Math.max(active.bandX - 70, 0), width - 170)
          }}
        >
          <div style={styles.tooltipDate}>{formatDate(active.date)}</div>
          <div style={styles.tooltipRow}>
            <span
              style={{ ...styles.tooltipKey, background: COMMUNITY_COLOR }}
            />
            <span style={styles.tooltipValue}>
              {formatTokens(active.communityTokens)}
            </span>
            <span style={styles.tooltipLabel}>community</span>
          </div>
          <div style={styles.tooltipRow}>
            <span
              style={{ ...styles.tooltipKey, background: COMMERCIAL_COLOR }}
            />
            <span style={styles.tooltipValue}>
              {formatTokens(active.commercialTokens)}
            </span>
            <span style={styles.tooltipLabel}>commercial</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const styles = {
  container: {
    position: 'relative',
    width: '100%'
  },
  svg: {
    display: 'block',
    overflow: 'visible'
  },
  empty: {
    color: MUTED_INK
  },
  tabular: {
    fontVariantNumeric: 'tabular-nums'
  },
  hit: {
    cursor: 'pointer',
    outline: 'none'
  },
  tooltip: {
    position: 'absolute',
    top: 0,
    width: '170px',
    padding: '8px 10px',
    background: SURFACE,
    border: `1px solid ${AXIS}`,
    borderRadius: '6px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
    fontSize: '12px',
    pointerEvents: 'none',
    zIndex: 1
  },
  tooltipDate: {
    color: MUTED_INK,
    marginBottom: '4px'
  },
  tooltipRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  },
  tooltipKey: {
    width: '10px',
    height: '2px',
    borderRadius: '1px',
    display: 'inline-block'
  },
  tooltipValue: {
    color: INK,
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums'
  },
  tooltipLabel: {
    color: MUTED_INK
  }
};

export default TokenSplitChart;
